/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CheckCircle2, Plus } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Payslip, type PayrollStatus } from '../../lib/api/payroll.js';
import { usePayrollRun, useProcessPayrollRun } from '../../lib/queries/payroll.js';
import { formatInr } from '../../lib/utils/money.js';

import { PayslipForm } from './payslip-form.js';

const STATUS_TONE: Readonly<Record<PayrollStatus, BadgeTone>> = {
  draft: 'slate',
  processed: 'violet',
  paid: 'green',
};

interface PayslipsTableProps {
  readonly payslips: readonly Payslip[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

function PayslipsTable({ payslips, isLoading, isError }: PayslipsTableProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={payslips.length === 0}
      loadingLabel={t('common.loading')}
      errorLabel={t('common.loadError')}
      emptyLabel={t('payroll.detail.empty')}
    >
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
          <tr>
            <th className="py-2 font-semibold">{t('payroll.detail.colTherapist')}</th>
            <th className="py-2 font-semibold">{t('payroll.detail.colRole')}</th>
            <th className="py-2 text-right font-semibold">{t('payroll.detail.colSessions')}</th>
            <th className="py-2 text-right font-semibold">{t('payroll.detail.colRate')}</th>
            <th className="py-2 text-right font-semibold">{t('payroll.detail.colGross')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {payslips.map((slip) => (
            <tr key={slip.id}>
              <td className="py-2 font-medium text-slate-800 dark:text-white">
                {slip.therapistName}
              </td>
              <td className="py-2 text-slate-500">{t('payroll.detail.role')}</td>
              <td className="py-2 text-right text-slate-600 dark:text-slate-300">
                {slip.sessionsCount}
              </td>
              <td className="py-2 text-right text-slate-600 dark:text-slate-300">
                {slip.sessionsCount > 0
                  ? formatInr(Math.round(slip.grossPaise / slip.sessionsCount))
                  : '—'}
              </td>
              <td className="py-2 text-right font-semibold text-slate-800 dark:text-slate-100">
                {formatInr(slip.grossPaise)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  );
}

interface RunDetailPanelProps {
  readonly runId: string;
}

/** Selected payroll run: payslips table + add-payslip + process (Figma). */
export function RunDetailPanel({ runId }: RunDetailPanelProps): JSX.Element {
  const { t } = useTranslation();
  const run = usePayrollRun(runId);
  const process = useProcessPayrollRun();
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onProcess = useCallback(() => {
    process.mutate(runId);
  }, [process, runId]);

  const data = run.data;
  const isDraft = data?.status === 'draft';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-lg font-semibold text-slate-800 dark:text-white">
            {data?.periodLabel ?? t('payroll.detail.title')}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('payroll.detail.total', { amount: formatInr(data?.totalPaise ?? 0) })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data !== undefined && (
            <Badge label={t(`payrollStatus.${data.status}`)} tone={STATUS_TONE[data.status]} />
          )}
          {isDraft && (
            <>
              <Button variant="outline" size="sm" onClick={openModal}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('payroll.detail.addPayslip')}
              </Button>
              <Button size="sm" onClick={onProcess} disabled={process.isPending}>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {t('payroll.detail.process')}
              </Button>
            </>
          )}
        </div>
      </div>

      <PayslipsTable
        payslips={data?.payslips ?? []}
        isLoading={run.isLoading}
        isError={run.isError}
      />

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('payroll.detail.addPayslip')}
        description={t('payroll.detail.addSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <PayslipForm runId={runId} onDone={closeModal} />
      </Modal>
    </div>
  );
}
